import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { InputParam } from "@/queries/models/types";
import { RadioGroup } from "@/components/ui/radio-group";
import { NumberConstraints } from "./NumberConstraints";
import { motion } from "framer-motion";
import { BuilderProps } from "./types";
import { handleNumberChange } from "./utils";
import { ParameterValue } from "@/components/ModelParameters/utils";

export const InputParameterBuilder = observer(
  ({ parameter, isViewing }: BuilderProps) => {
    const [inputType, setInputType] = useState("text");

    useEffect(() => {
      if (!parameter) return;

      if (parameter.min !== undefined || parameter.max !== undefined) {
        setInputType("number");
      } else {
        setInputType("text");
      }
    }, [parameter]);

    const updateInputParameter = (updates: Partial<InputParam>) => {
      if (!parameter) return;

      const baseParam = parameter.getParameter as InputParam;
      parameter.updateParameter({
        ...baseParam,
        ...updates,
      });
    };

    const handleInputTypeChange = (value: string) => {
      setInputType(value);

      if (value === "number") {
        if (parameter.min === undefined && parameter.max === undefined) {
          updateInputParameter({
            default: 0,
            min: 0,
            max: 100,
            is_int: false,
          });
        }
      } else if (value === "text") {
        updateInputParameter({
          default: "Example Default Value",
          min: undefined,
          max: undefined,
          is_int: false,
        });
      }
    };

    useEffect(() => {
      if (parameter.isInt) {
        parameter.updateDefault(parameter.default || 0);
        parameter.updateMin(Number(parameter.min) || 0);
        parameter.updateMax(Number(parameter.max) || 0);
      }
    }, [parameter.isInt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTextChange = (value: string) => {
      parameter.updateDefault(value);
    };

    const handleDefaultChange = (value: string) => {
      if (inputType === "number") {
        const newValue = handleNumberChange(value, parameter.isInt);
        const numValue = Number(value);

        if (parameter.min !== undefined && numValue < Number(parameter.min)) {
          parameter.updateDefault(parameter.min);
          return;
        }

        if (parameter.max !== undefined && numValue > Number(parameter.max)) {
          parameter.updateDefault(parameter.max);
          return;
        }

        parameter.updateDefault(newValue as ParameterValue);
      } else {
        handleTextChange(value);
      }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (inputType === "number" && isNaN(Number(e.target.value))) {
        parameter.updateDefault(0);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        layout
        transition={{
          duration: 0.3,
          layout: {
            duration: 0.3,
            ease: "easeOut",
          },
        }}
        className="space-y-6 overflow-hidden"
      >
        <div className="space-y-3">
          <Label htmlFor="inputType" className="text-sm font-medium">
            Input Type
          </Label>
          <RadioGroup
            className="flex flex-row gap-4"
            name="inputType"
            items={[
              {
                label: "Text",
                value: "text",
                id: "text",
              },
              {
                label: "Number",
                value: "number",
                id: "number",
              },
            ]}
            defaultValue={inputType}
            value={inputType}
            onValueChange={handleInputTypeChange}
            disabled={isViewing}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="placeholder" className="text-sm font-medium">
            Placeholder
          </Label>
          <Input
            id="placeholder"
            value={parameter.placeholder || ""}
            onChange={(e) => parameter.updatePlaceholder(e.target.value)}
            readOnly={isViewing}
            className="w-full"
            placeholder={
              inputType === "text" ? "Enter text..." : "Enter a number..."
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="defaultValue" className="text-sm font-medium">
            Default Value
          </Label>
          <Input
            id="defaultValue"
            type="text"
            value={parameter.default?.toString() || ""}
            onChange={(e) => handleDefaultChange(e.target.value)}
            readOnly={isViewing}
            onBlur={onBlur}
          />
        </div>
        <NumberConstraints
          inputType={inputType}
          parameter={parameter}
          isViewing={isViewing}
        />
      </motion.div>
    );
  },
);
